import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RiArrowRightSLine } from "react-icons/ri";
import {
  getRequest,
  putRequest,
  putWithFormData,
} from "../../helpers/api.helper";

const Step8Edit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { profile } = location.state || {};

  // Add profile check
  if (!profile) {
    return (
      <div className="flex justify-center items-center h-screen text-red-600 font-semibold">
        ⚠️ Profile data not available.
      </div>
    );
  }

  /** ---------- State Management ---------- **/
  const [modalData, setModalData] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [city, setCity] = useState([]);

  /** ---------- Fetch Dropdown Data ---------- **/
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRequest("/common/profile/step8");
        if (response.success) {
          setCountries(response.data.country || []);
          setStates(response.data.state || []);
          setCity(response.data.city);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Initialize with profile data
  const [selectedOptions, setSelectedOptions] = useState<any>({
    country: profile?.country
      ? { value: profile.country, label: profile.country }
      : null,
    state: profile?.state
      ? { value: profile.state, label: profile.state }
      : null,
    city: profile?.district
      ? { value: profile.district, label: profile.district }
      : null,
  });

  /** ---------- Dropdown Options ---------- **/
  const dropdownOptions: any = {
    country: {
      type: "single",
      options: countries.map((country: any) => ({
        value: country.country,
        label: country.country,
      })),
    },
    state: {
      type: "single",
      options: states.map((state: any) => ({
        value: state.state,
        label: state.state,
      })),
    },
    city: {
      type: "single",
      options: city.map((c: any) => ({
        value: c.city,
        label: c.city,
      })),
    },
  };

  /** ---------- Effects ---------- **/
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (modalData) {
      setSearchTerm("");
    }
  }, [modalData]);

  /** ---------- Handlers ---------- **/
  const openModal = (key: any) => {
    setModalData({
      key,
      type: dropdownOptions[key].type,
      options: dropdownOptions[key].options,
    });
  };

  const closeModal = () => {
    setModalData(null);
    setSearchTerm("");
  };

  const handleSingleSelect = (option: any) => {
    setSelectedOptions((prev: any) => ({
      ...prev,
      [modalData.key]: option,
    }));
    closeModal();
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const formDataToSend = {
      country: selectedOptions.country?.value || "",
      state: selectedOptions.state?.value || "",
      district: selectedOptions.city?.value || "",
      step1: 1,
      step2: 1,
      step3: 1,
      step4: 1,
      step5: 1,
      step6: 1,
      step7: 1,
      step8: 1,
    };

    try {
      const response = await putRequest(
        `/bureau/profile/${profile._id}`,
        formDataToSend
      );
      if (response.success) {
        alert("Details updated successfully!");
        navigate(-1);
      } else {
        alert("Failed to update details.");
      }
    } catch (error) {
      console.error("Error updating details:", error);
      alert("An error occurred while updating details.");
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  /** ---------- Helper Functions ---------- **/
  const getDisplayValue = (key: any) => {
    const value = selectedOptions[key];
    return value?.label || `Select ${key.replace(/_/g, " ")}`;
  };

  /** ---------- Reusable Modal Component ---------- **/
  const Modal = () => {
    if (!modalData) return null;

    const filteredOptions = modalData.options.filter((option: any) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full shadow-lg m-5">
          <h2 className="text-lg font-bold mb-4 capitalize text-center">
            Select {modalData.key.replace(/_/g, " ")}
          </h2>

          {/* Search Bar */}
          <input
            type="text"
            autoFocus
            className="w-full px-4 py-2 mb-4 border border-gray-300 rounded focus:outline-none focus:ring focus:ring-blue-200"
            placeholder={`Search ${modalData.key.replace(/_/g, " ")}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* Options List */}
          <div className="h-64 overflow-y-auto border-t border-b border-gray-200">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option: any, index: any) => (
                <React.Fragment key={option.value}>
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition"
                    onClick={() => handleSingleSelect(option)}
                  >
                    {option.label}
                  </button>
                  {index < filteredOptions.length - 1 && (
                    <hr className="border-gray-200" />
                  )}
                </React.Fragment>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                No options found
              </div>
            )}
          </div>

          {/* Close Button */}
          <button
            className="mt-4 w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
            onClick={closeModal}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  /** ---------- Render ---------- **/
  return (
    <div className="profile-edit-page flex justify-center items-center min-h-screen bg-gradient-to-r from-blue-50 to-indigo-100">
      <div className="w-full max-w-lg bg-white shadow-lg rounded-lg">
        {/* Header */}
        <div className="w-full z-50 bg-red-600 fixed top-0 mb-12 p-2 px-3 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <button className="flex items-center" onClick={handleBackClick}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-center text-white py-3">
              Step 8 -
            </h1>
            <h5 className="font-semibold text-white">Location Details</h5>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 mt-20 px-4 py-4">
          {/* All Fields */}
          {Object.keys(dropdownOptions).map((key) => (
            <div key={key}>
              <label className="block text-red-500 font-medium mb-2 capitalize">
                {key === "city" ? "District" : key} *
              </label>
              <div
                className="flex flex-col w-full border px-3 border-gray-300 py-3 cursor-pointer rounded-lg hover:shadow-lg transition-all"
                onClick={() => openModal(key)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-md font-medium">
                    {getDisplayValue(key)}
                  </span>
                  <RiArrowRightSLine className="text-3xl text-gray-500 ml-3" />
                </div>
              </div>
            </div>
          ))}

          <button
            type="submit"
            className="w-full bg-red-600 text-white p-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
          >
            Update Location Details
          </button>
        </form>

        {/* Reusable Modal */}
        <Modal />
      </div>
    </div>
  );
};

export default Step8Edit;
